import { Day, Schedule, TimeRange, DragState } from "./types";
import {
  DAYS,
  TOTAL_SLOTS,
  NIGHT_EVENING_START,
  NIGHT_MORNING_END,
} from "./constants";

// --- Helpers ---

export function timeToSlotIndex(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 2 + (m >= 30 ? 1 : 0);
}

export function slotIndexToTime(index: number): string {
  const h = Math.floor(index / 2);
  const m = index % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
}

export function getDayEnabledRange(
  day: Day,
  weekdaysEnabled: boolean,
  weekendEnabled: boolean
): { minSlot: number; maxSlot: number } | null {
  if (!weekdaysEnabled && !weekendEnabled) return null;
  if (weekdaysEnabled && weekendEnabled) return { minSlot: 0, maxSlot: TOTAL_SLOTS };

  let min = TOTAL_SLOTS;
  let max = 0;

  if (weekendEnabled) {
    switch (day.key) {
      case "fri": min = Math.min(min, 30); max = Math.max(max, TOTAL_SLOTS); break;
      case "sat": min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "sun": min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "mon": min = Math.min(min, 0); max = Math.max(max, 16); break;
    }
  }

  if (weekdaysEnabled) {
    switch (day.key) {
      case "sun": min = Math.min(min, 42); max = Math.max(max, TOTAL_SLOTS); break;
      case "mon": case "tue": case "wed": case "thu": case "fri":
        min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "sat": min = Math.min(min, 0); max = Math.max(max, 30); break;
    }
  }

  if (min >= max) return null;
  return { minSlot: min, maxSlot: max };
}

export function isDayDisabled(day: Day, weekdaysEnabled: boolean, weekendEnabled: boolean): boolean {
  return getDayEnabledRange(day, weekdaysEnabled, weekendEnabled) === null;
}

export function getAvailableCorridor(
  events: TimeRange[],
  excludeIndex: number,
  anchorStart: number,
  anchorEnd: number
): { min: number; max: number } {
  let min = 0;
  let max = TOTAL_SLOTS;
  events.forEach((event, idx) => {
    if (idx === excludeIndex) return;
    const s = timeToSlotIndex(event[0]);
    const e = timeToSlotIndex(event[1]);
    if (e <= anchorStart) min = Math.max(min, e);
    if (s >= anchorEnd) max = Math.min(max, s);
  });
  return { min, max };
}

export function computePreviewEvent(
  drag: DragState,
  dayEvents: TimeRange[],
  range?: { minSlot: number; maxSlot: number } | null
): { start: number; end: number } {
  const excludeIndex = drag.mode === "create" ? -1 : drag.eventIndex;
  const anchorStart = drag.mode === "create" ? drag.initialSlotIndex : drag.originalStart;
  const anchorEnd = drag.mode === "create" ? drag.initialSlotIndex + 1 : drag.originalEnd;
  const corridor = getAvailableCorridor(dayEvents, excludeIndex, anchorStart, anchorEnd);
  const cMin = range ? Math.max(corridor.min, range.minSlot) : corridor.min;
  const cMax = range ? Math.min(corridor.max, range.maxSlot) : corridor.max;

  switch (drag.mode) {
    case "create": {
      const rawMin = Math.min(drag.initialSlotIndex, drag.currentSlotIndex);
      const rawMax = Math.max(drag.initialSlotIndex, drag.currentSlotIndex);
      return { start: Math.max(cMin, rawMin), end: Math.min(cMax, rawMax + 1) };
    }
    case "move": {
      const duration = drag.originalEnd - drag.originalStart;
      const offset = drag.initialSlotIndex - drag.originalStart;
      let newStart = drag.currentSlotIndex - offset;
      newStart = Math.max(cMin, Math.min(cMax - duration, newStart));
      return { start: newStart, end: newStart + duration };
    }
    case "resize-top": {
      const newStart = Math.max(cMin, Math.min(drag.originalEnd - 1, drag.currentSlotIndex));
      return { start: newStart, end: drag.originalEnd };
    }
    case "resize-bottom": {
      const newEnd = Math.min(cMax, Math.max(drag.originalStart + 1, drag.currentSlotIndex + 1));
      return { start: drag.originalStart, end: newEnd };
    }
  }
}

export function canCopyAllEventsToDay(
  sourceEvents: TimeRange[],
  targetRange: { minSlot: number; maxSlot: number },
  existingEvents: TimeRange[]
): boolean {
  let hasFittingEvent = false;
  for (const event of sourceEvents) {
    const s = Math.max(timeToSlotIndex(event[0]), targetRange.minSlot);
    const e = Math.min(timeToSlotIndex(event[1]), targetRange.maxSlot);
    if (s >= e) continue;
    hasFittingEvent = true;
    const collides = existingEvents.some((r) => {
      const rs = timeToSlotIndex(r[0]);
      const re = timeToSlotIndex(r[1]);
      return s < re && rs < e;
    });
    if (collides) return false;
  }
  return hasFittingEvent;
}

export function copyEventsToDay(
  sourceEvents: TimeRange[],
  targetRange: { minSlot: number; maxSlot: number }
): TimeRange[] {
  const result: TimeRange[] = [];
  for (const event of sourceEvents) {
    const s = Math.max(timeToSlotIndex(event[0]), targetRange.minSlot);
    const e = Math.min(timeToSlotIndex(event[1]), targetRange.maxSlot);
    if (s >= e) continue;
    result.push([slotIndexToTime(s), slotIndexToTime(e)]);
  }
  return result;
}

// --- Night helpers ---

// Cada noche que arranca en `startDayKeys` ocupa 21:00-24:00 ese día y 00:00-09:00 del día siguiente.
// Los segmentos se recortan al rango habilitado de cada día (los días deshabilitados se omiten).
// Si un segmento colisiona con un evento existente no se crea y no se modifica nada de ese día.
export function applyNightSchedule(
  value: Schedule,
  startDayKeys: string[],
  weekdaysEnabled: boolean,
  weekendEnabled: boolean
): Schedule {
  const segments: Record<string, [number, number][]> = {};
  const addSegment = (key: string, start: number, end: number) => {
    (segments[key] ||= []).push([start, end]);
  };
  startDayKeys.forEach((key) => {
    const idx = DAYS.findIndex((d) => d.key === key);
    const nextKey = DAYS[(idx + 1) % DAYS.length].key;
    addSegment(key, NIGHT_EVENING_START, TOTAL_SLOTS);
    addSegment(nextKey, 0, NIGHT_MORNING_END);
  });

  const result: Schedule = { ...value };
  DAYS.forEach((day) => {
    const segs = segments[day.key];
    if (!segs) return;
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const dayEvents = [...(result[day.key] || [])];
    segs.forEach(([s, e]) => {
      const cs = Math.max(s, range.minSlot);
      const ce = Math.min(e, range.maxSlot);
      if (cs >= ce) return;
      const collides = dayEvents.some(([rStart, rEnd]) => {
        const rs = timeToSlotIndex(rStart);
        const re = timeToSlotIndex(rEnd);
        return cs < re && rs < ce;
      });
      if (collides) return;
      dayEvents.push([slotIndexToTime(cs), slotIndexToTime(ce)]);
    });
    if (dayEvents.length) result[day.key] = dayEvents;
  });
  return result;
}

// Elimina los eventos cuya hora de inicio y fin queden dentro de la franja nocturna (21:00-09:00).
export function clearNightEvents(value: Schedule): Schedule {
  const result: Schedule = {};
  DAYS.forEach((day) => {
    const events = value[day.key];
    if (!events?.length) return;
    const kept = events.filter(([start, end]) => {
      const s = timeToSlotIndex(start);
      const e = timeToSlotIndex(end);
      const isNight = s >= NIGHT_EVENING_START || e <= NIGHT_MORNING_END;
      return !isNight;
    });
    if (kept.length) result[day.key] = kept;
  });
  return result;
}
